import React from 'react';

import axios from 'axios';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import WaitingBlock from '@/lib/components/WaitingBlock';

import PaylineWebPaymentWidget from './PaylineWebPaymentWidget';

import './PaylineWebPayment.scss';
import { getClientCapabilities } from '@/lib/utils/browserUtils.js';
import Loader from '../../Loader';

const PaylineWebPayment = React.forwardRef(
    (
        {
            config,
            token,
            paymentRequestId,
            lyriaUrl,
            onPaymentResult,
            onPaymentStarted,
            loader,
            paymentMethodId,
        },
    ) => {
        const { t } = useTranslation();
        const [isProcessed, setIsProcessed] = React.useState(false);
        const [isFinalStateReached, setIsFinalStateReached] = React.useState(false);
        const [isWaitingConfirmation, setIsWaitingConfirmation] = React.useState(false);
        const [isWidgetLoading, setIsWidgetLoading] = React.useState(true);
        const process = React.useCallback(
            async () => {
                onPaymentStarted();
                await axios({
                    method: 'post',
                    url: lyriaUrl + '/payline-web-payment/process',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    data: {
                        client_capabilities: getClientCapabilities(),
                        request_token: token,
                        payment_request_id: paymentRequestId,
                        psp: paymentMethodId,
                        data: {},
                    },
                }).catch(error => {
                    onPaymentResult({ success: false, data: error?.response?.data?.data || { code: 'internal' } });
                });
                setIsProcessed(true);
            },
            [onPaymentStarted, onPaymentResult, lyriaUrl, paymentRequestId, paymentMethodId, token],
        );

        const onFinalState = React.useCallback(() => {
            setIsFinalStateReached(true);
            return false;
        }, [setIsFinalStateReached]);

        const onDidShowState = React.useCallback(() => {
            setIsWidgetLoading(false);
        }, []);

        React.useEffect(() => {
            if (!isProcessed || !isFinalStateReached) {
                return;
            }
            setIsWaitingConfirmation(true);
            axios({
                method: 'get',
                url: config.merchant_return_url,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            }).then(response => {
                setIsWaitingConfirmation(false);
                if (response.data?.data?.message?.status === 'error') {
                    onPaymentResult({
                        success: false,
                        data: { code: response?.data?.data?.message?.data?.code || 'internal' },
                    });
                } else {
                    onPaymentResult({ success: true, data: response.data?.data });
                }
            }).catch(error => {
                setIsWaitingConfirmation(false);
                onPaymentResult({ success: false, error: error });
            });
        }, [onPaymentResult, isProcessed, isFinalStateReached, config.merchant_return_url]);

        return (
            <div id="payline">
                {isWidgetLoading && (
                    <Loader />
                )}
                {isWaitingConfirmation && (
                    <WaitingBlock loader={loader}>
                        <div>{t('waitingPaymentResult')}</div>
                    </WaitingBlock>
                )}
                <PaylineWebPaymentWidget
                    widgetToken={config.widget_token}
                    merchantReturnUrl={config.merchant_return_url}
                    environment={config.widget_environment}
                    template="column"
                    onFinalState={onFinalState}
                    onBeforePayment={process}
                    onDidShowState={onDidShowState}
                />
            </div>
        );
    },
);

PaylineWebPayment.propTypes = {
    config: PropTypes.object.isRequired,
    token: PropTypes.string.isRequired,
    paymentRequestId: PropTypes.string,
    lyriaUrl: PropTypes.string.isRequired,
    onPaymentResult: PropTypes.func.isRequired,
    onPaymentStarted: PropTypes.func.isRequired,
    paymentMethodId: PropTypes.number.isRequired,
    loader: PropTypes.node,
    isDisabled: PropTypes.bool,
};

PaylineWebPayment.defaultProps = {
    paymentRequestId: undefined,
    loader: null,
    isDisabled: false,
};

PaylineWebPayment.displayName = 'PaylineWebPayment';

export default PaylineWebPayment;
